import { initializeApp, deleteApp } from 'firebase/app';
import { createUserWithEmailAndPassword, deleteUser, getAuth, sendEmailVerification, signInWithCustomToken, signOut as firebaseSignOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  collection, deleteDoc, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, setDoc, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { app, auth, db, functions } from '../../config/firebase';
import { friendlyError } from '../../utils/userMessages';

const normalizeCallableError = (error, name) => {
  const message = friendlyError(error, 'The service could not complete your request. Please try again.');
  const enhanced = new Error(message); enhanced.code = String(error?.code || 'functions/unknown').toLowerCase(); enhanced.operation = name || ''; enhanced.cause = error; return enhanced;
};
const call = async (name, data = {}) => { try { const callable = httpsCallable(functions, name, { timeout: 60000 }); const result = await callable(data); return result.data; } catch (error) { throw normalizeCallableError(error, name); } };
const currentUid=()=>{const uid=auth.currentUser?.uid;if(!uid)throw new Error('Sign-in is required.');return uid;};
const normalizeRole=(role)=>String(role||'').toLowerCase().replace(/[\s-]+/g,'_');
async function requireCompanyAdmin(companyId){
  const uid=currentUid();
  const snap=await getDoc(doc(db,'users',uid));
  if(!snap.exists())throw new Error('Your company account could not be found.');
  const data=snap.data()||{};
  const sameCompany=String(data.companyId||'')===String(companyId||'');
  const allowed=data.isOwner===true||['admin','company_admin','owner'].includes(normalizeRole(data.role||data.userRole));
  if(!sameCompany||!allowed||data.isBlocked===true)throw new Error('Only a company administrator can perform this action.');
  return {uid,data};
}

const COMPANY_MEMBER_ROLES = new Set(['admin', 'manager', 'employee', 'viewer']);
const sanitizeCompanyMemberRole = (role) => {
  const normalized = normalizeRole(role || 'employee');
  if (!COMPANY_MEMBER_ROLES.has(normalized)) throw new Error('Invalid company member role.');
  return normalized;
};
const sanitizeCompanyPermissions = (permissions) => Array.from(new Set((Array.isArray(permissions) ? permissions : ['read'])
  .map((value) => String(value || '').trim().toLowerCase())
  .filter((value) => value && value.length <= 64 && !/(super.?admin|platform.?admin|system.?admin)/i.test(value))))
  .slice(0, 30);

const safeGatewayUrl=(value='')=>{const raw=String(value||'').trim();if(!raw)throw new Error('The required external gateway is not configured by Super Admin.');let parsed;try{parsed=new URL(raw);}catch(_){throw new Error('The configured gateway URL is invalid.');}const local=['localhost','127.0.0.1','::1'].includes(parsed.hostname);if(parsed.protocol!=='https:'&&!(local&&parsed.protocol==='http:'))throw new Error('Gateway URL must use HTTPS (localhost HTTP is allowed only for development).');return parsed.toString();};

export async function getPublicPlatformRuntime(){const snap=await getDoc(doc(db,'systemSettings','public_runtime'));return snap.exists()?snap.data()||{}:{};}
export async function getIntegrationSettings(){const runtime=await getPublicPlatformRuntime();return{settings:{storage:runtime.storage||{provider:'firebase'},payments:runtime.payments||{enabled:false},email:{enabled:Boolean(runtime.notifications?.email)},push:{enabled:Boolean(runtime.notifications?.firebasePush||runtime.notifications?.pushEnabled)},ai:{enabled:Boolean(runtime.features?.ai)},agora:{enabled:Boolean(runtime.meeting?.agoraEnabled),appId:runtime.meeting?.agoraAppId||''},calendar:{enabled:Boolean(runtime.meeting?.calendarEnabled)},whatsapp:runtime.whatsapp||{}},configured:{},backendAvailable:false,directFirestore:true};}

export async function sendTransactionalEmail(companyId,payload={}){
  const runtime=await getPublicPlatformRuntime();
  if(!runtime.notifications?.email)throw new Error('Email notifications are disabled by Super Admin.');
  const endpoint=runtime.email?.gatewayUrl||'';
  return postPublicGateway(endpoint,{action:'send_email',companyId,...payload},'Unable to send this email.');
}
export async function sendOperationEmail(companyId,templateKey,to,variables={}){
  const runtime=await getPublicPlatformRuntime();
  if(!runtime.notifications?.email)throw new Error('Email notifications are disabled by Super Admin.');
  const endpoint=runtime.email?.gatewayUrl||'';
  return postPublicGateway(endpoint,{action:'send_operation_email',companyId,templateKey,to,variables},'Unable to send this email.');
}
export async function sendSms(companyId,payload={}){
  const runtime=await getPublicPlatformRuntime();
  if(!runtime.notifications?.sms)throw new Error('SMS notifications are disabled by Super Admin.');
  const endpoint=runtime.sms?.gatewayUrl||'';
  return postPublicGateway(endpoint,{action:'send_sms',companyId,...payload},'Unable to send this SMS.');
}
export async function sendWhatsAppNotification(companyId,payload={}){
  const runtime=await getPublicPlatformRuntime();
  if(!runtime.notifications?.whatsapp)throw new Error('WhatsApp notifications are disabled by Super Admin.');
  const endpoint=runtime.whatsapp?.gatewayUrl||'';
  return postPublicGateway(endpoint,{action:'send_notification',companyId,...payload},'Unable to send this WhatsApp notification.');
}
export const createPaymentOrder=(companyId,payload)=>call('createPaymentOrder',{companyId,...payload});
export const getAgoraRtcToken=(companyId,payload)=>call('getAgoraRtcToken',{companyId,...payload});
export const markMeetingJoined=(meetingId)=>call('markMeetingJoined',{meetingId});
export const generateAiDraft=(companyId,payload)=>call('generateAiDraft',{companyId,...payload});
export const analyzeBusinessMatch=(companyId,payload)=>call('analyzeBusinessMatch',{companyId,...payload});
export const createIntroduction=(companyId,payload)=>call('createIntroduction',{companyId,...payload});
export const respondToIntroduction=(companyId,payload)=>call('respondToIntroduction',{companyId,...payload});
export const requestCalendarAvailability=(companyId,payload)=>call('requestCalendarAvailability',{companyId,...payload});
export const getInteractionPermission=(companyId,payload)=>call('getInteractionPermission',{companyId,...payload});
export const bookMeeting=(companyId,payload)=>call('bookMeeting',{companyId,...payload});
export const requestMeetingChange=(companyId,payload)=>call('requestMeetingChange',{companyId,...payload});
export const respondMeetingChange=(companyId,payload)=>call('respondMeetingChange',{companyId,...payload});
export const cancelMeeting=(companyId,payload)=>call('cancelMeeting',{companyId,...payload});
export const refreshBusinessMatches=(companyId)=>call('refreshBusinessMatches',{companyId});

const COMPANY_PROFILE_EDITABLE_FIELDS = new Set([
  'companyName', 'companyType', 'description', 'website', 'country', 'countryCode', 'dialCode', 'email', 'phone', 'linkedin',
  'logo', 'logoUrl', 'companyLogo', 'companyLogoUrl', 'logoAsset', 'coverImage', 'coverImageUrl', 'companyCover', 'companyCoverUrl', 'coverAsset',
  'specialization', 'primaryRegionAvailability', 'secondaryRegionAvailability', 'preferredRegion', 'secondaryPreferredRegion',
  'primaryServiceProvided', 'secondaryServiceProvided', 'primaryServiceBuying', 'secondaryServiceBuying',
  'servicesProvided', 'regionPriorities', 'lookingFor', 'primaryConnectWith', 'secondaryConnectWith', 'otherConnectWith',
  'departments', 'designations',
  'name', 'primaryService', 'secondaryService', 'primaryRegion', 'secondaryRegion', 'companySize', 'city', 'address', 'yearFounded', 'emailDomain',
  'facebook', 'twitter', 'instagram', 'youtube', 'whatsapp', 'telegram', 'branding',
  'timezone', 'dateFormat', 'language', 'workingHours', 'defaultMeetingDuration', 'meetingTypes', 'autoAcceptMeetings', 'requireMeetingAgenda',
  'allowRecording', 'allowChat', 'maxParticipants', 'bufferTime', 'emailNotifications', 'pushNotifications', 'smsNotifications',
  'meetingReminders', 'meetingConfirmations', 'meetingCancellations',
  'allowPublicProfile', 'showInSearch', 'enableAnalytics', 'autoSyncCalendar', 'meetingRoomPrefix',
]);
export async function updateCompanyProfile(companyId, changes = {}) {
  await requireCompanyAdmin(companyId);
  const safeChanges = Object.fromEntries(Object.entries(changes || {}).filter(([key, value]) => COMPANY_PROFILE_EDITABLE_FIELDS.has(key) && value !== undefined));
  if (!Object.keys(safeChanges).length) throw new Error('No editable company profile fields were provided.');
  await setDoc(doc(db, 'companies', companyId), { ...safeChanges, updatedAt: serverTimestamp() }, { merge: true });
  return { success: true, changes: safeChanges };
}

export async function updateCompanyExtraSeatConfig(companyId, config = {}) {
  await requireCompanyAdmin(companyId);
  const price = Number(config.price || 0);
  if (!Number.isFinite(price) || price < 0) throw new Error('Additional user price must be zero or greater.');
  const paymentLink = String(config.paymentLink || '').trim();
  if (paymentLink && !/^https:\/\//i.test(paymentLink)) throw new Error('Payment link must use HTTPS.');
  const companyRef = doc(db, 'companies', companyId);
  const companySnap = await getDoc(companyRef);
  if (!companySnap.exists()) throw new Error('Company was not found.');
  await updateDoc(companyRef, {
    'subscription.extraSeatPrice': price,
    'subscription.extraSeatCurrency': String(config.currency || 'EUR').trim().toUpperCase().slice(0, 8),
    'subscription.extraSeatPaymentLink': paymentLink,
    'subscription.extraSeatPaymentInstructions': String(config.instructions || '').trim().slice(0, 2000),
    updatedAt: serverTimestamp(),
  });
  return { success: true };
}

export async function requestWhatsAppOtp(phone,purpose='verification'){
  const runtime=await getPublicPlatformRuntime();const endpoint=runtime.whatsapp?.requestOtpUrl||runtime.whatsapp?.gatewayUrl||'';
  if(!endpoint)throw new Error('WhatsApp OTP is not configured by Super Admin.');
  const response=await fetch(safeGatewayUrl(endpoint),{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({channel:'whatsapp',action:'request_otp',operation:'request_otp',phone,purpose})});const body=await response.json().catch(()=>({}));if(!response.ok||body?.success===false)throw new Error(body.message||`WhatsApp gateway returned HTTP ${response.status}.`);return body;
}
export async function verifyWhatsAppOtp(phone,otp,purpose='verification'){
  const runtime=await getPublicPlatformRuntime();const endpoint=runtime.whatsapp?.verifyOtpUrl||runtime.whatsapp?.gatewayUrl||'';
  if(!endpoint)throw new Error('WhatsApp OTP verification is not configured by Super Admin.');
  const response=await fetch(safeGatewayUrl(endpoint),{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({channel:'whatsapp',action:'verify_otp',operation:'verify_otp',phone,otp,purpose})});const body=await response.json().catch(()=>({}));if(!response.ok||body?.success===false||body?.ok===false||body?.verified===false)throw new Error(body.message||'WhatsApp OTP is invalid or expired.');if(body.firebaseCustomToken)await signInWithCustomToken(auth,body.firebaseCustomToken);return body;
}

async function postPublicGateway(endpoint, body, fallback='The configured service could not complete the request.'){
  const safeEndpoint=safeGatewayUrl(endpoint);
  const response=await fetch(safeEndpoint,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)});
  const result=await response.json().catch(()=>({}));
  if(!response.ok||result?.success===false)throw new Error(result?.message||`${fallback} (HTTP ${response.status})`);
  return result;
}
export async function sendProfessionalVerificationEmail(payload={}){
  const runtime=await getPublicPlatformRuntime();
  if(runtime.auth?.verificationProvider==='whatsapp')return requestWhatsAppOtp(payload.phone||'', 'verification');
  if(runtime.auth?.verificationProvider==='firebase_auth')throw new Error('Firebase verification must be sent from the authenticated Firebase session.');
  const endpoint=runtime.email?.verificationGatewayUrl||runtime.email?.gatewayUrl||'';
  if(runtime.auth?.verificationProvider==='manual')return {success:true,provider:'manual',pendingManualVerification:true};
  return postPublicGateway(endpoint,{channel:'email',action:'send_verification',operation:'send_verification',...payload,appUrl:runtime.email?.verificationBaseUrl||window.location.origin},'Unable to send the professional verification email.');
}
export async function verifyProfessionalEmailToken(token){
  const runtime=await getPublicPlatformRuntime();
  const endpoint=runtime.email?.verificationGatewayUrl||runtime.email?.gatewayUrl||'';
  return postPublicGateway(endpoint,{channel:'email',action:'verify_token',operation:'verify_token',token},'Unable to verify this email link.');
}
export async function getEmailVerificationStatus(){
  const uid=currentUid();
  const [snap,runtime]=await Promise.all([getDoc(doc(db,'users',uid)),getPublicPlatformRuntime().catch(()=>({}))]);
  const data=snap.exists()?snap.data()||{}:{};
  const provider=runtime?.auth?.verificationProvider||'firebase_auth';
  const firebaseVerified=Boolean(auth.currentUser?.emailVerified);
  const verified=provider==='firebase_auth'
    ? firebaseVerified
    : provider==='whatsapp'
      ? Boolean(data.whatsappVerified||data.verificationOverride)
      : provider==='manual'
        ? Boolean(data.verificationOverride||data.isVerified)
        : Boolean(data.emailVerified||data.verificationOverride||data.isVerified);
  return{verified,firebaseVerified,provider,method:data.verificationMethod||''};
}
export async function requestProfessionalPasswordResetEmail(email,origin=window.location.origin){const runtime=await getPublicPlatformRuntime();const endpoint=runtime.email?.passwordResetGatewayUrl||runtime.email?.gatewayUrl||'';return postPublicGateway(endpoint,{channel:'email',action:'password_reset',operation:'password_reset_request',email,origin:runtime.email?.verificationBaseUrl||origin},'Unable to send the password reset email.');}
export const sendProfessionalPasswordResetEmail=(email,companyId='')=>requestProfessionalPasswordResetEmail(email,window.location.origin).then(result=>({...result,companyId}));

export async function listSubscriptionPlans(includeInactive=false){const snap=await getDocs(collection(db,'subscriptionPlans'));let plans=snap.docs.map(r=>({id:r.id,...r.data()}));if(!includeInactive)plans=plans.filter(p=>p.isActive!==false);plans.sort((a,b)=>Number(a.sortOrder||0)-Number(b.sortOrder||0)||Number(a.price||0)-Number(b.price||0));return{plans};}
export const saveSubscriptionPlan=()=>{throw new Error('Subscription plans are managed only in the Super Admin Portal.');};
export const setSubscriptionPlanActive=()=>{throw new Error('Subscription plans are managed only in the Super Admin Portal.');};

async function getSeatSummary(companyId){
  const [companySnap,userSnap]=await Promise.all([
    getDoc(doc(db,'companies',companyId)),
    getDocs(query(collection(db,'users'),where('companyId','==',companyId)))
  ]);
  if(!companySnap.exists())throw new Error('Company was not found.');
  const company=companySnap.data()||{};
  const storedSubscription=company.subscription||{};
  const planId=storedSubscription.packageId||storedSubscription.planId||company.packageId||company.subscriptionPlanId||'';
  let plan=null;
  if(planId){const planSnap=await getDoc(doc(db,'subscriptionPlans',planId));if(planSnap.exists())plan={id:planSnap.id,...planSnap.data()};}
  const capacitySource=plan||storedSubscription;
  const rawLimit=capacitySource.userLimit??capacitySource.maxUsers??storedSubscription.userLimit??storedSubscription.maxUsers??0;
  const limit=Number(rawLimit||0);
  const unlimited=Boolean(capacitySource.unlimitedUsers||limit===0);
  const allowExtraSeats=unlimited?false:(capacitySource.allowExtraSeats!==false);
  const subscription={...storedSubscription,...(plan||{}),packageId:plan?.id||planId||storedSubscription.packageId||null,userLimit:limit,maxUsers:limit,unlimitedUsers:unlimited,allowExtraSeats};
  const members=userSnap.docs.map(r=>({id:r.id,...r.data()}));
  const committedMembers=members.filter(u=>u.isBlocked!==true&&['active','reserved','pending_company_approval','pending_extra_seat_payment'].includes(String(u.seatStatus||'active')));
  const used=committedMembers.filter(u=>String(u.seatStatus||'active')==='active'&&u.isActive!==false).length;
  const reserved=committedMembers.length-used;
  return{company,subscription,plan,members,seats:{
    limit,unlimited,used,reserved,committed:committedMembers.length,
    available:unlimited?null:Math.max(0,limit-committedMembers.length),
    allowExtraSeats,
    extraSeatPrice:Number(storedSubscription.extraSeatPrice??company.extraSeatPrice??0),
    extraSeatCurrency:storedSubscription.extraSeatCurrency||company.extraSeatCurrency||storedSubscription.currency||plan?.currency||'EUR',
    extraSeatPaymentLink:storedSubscription.extraSeatPaymentLink||company.extraSeatPaymentLink||'',
    extraSeatPaymentInstructions:storedSubscription.extraSeatPaymentInstructions||company.extraSeatPaymentInstructions||''
  }};
}

export async function getCompanySeatStatus(companyId){const summary=await getSeatSummary(companyId);return{companyId,seats:summary.seats,subscription:summary.subscription};}
export async function getCompanySubscriptionStatus(companyId){const summary=await getSeatSummary(companyId);return{companyId,subscription:summary.subscription,paymentStatus:summary.company.paymentStatus||'',isActive:summary.company.isActive!==false,seats:summary.seats};}
export async function getCompanySubscriptionHistory(companyId){currentUid();const snap=await getDocs(collection(db,'companies',companyId,'subscriptionPayments'));const payments=snap.docs.map(r=>({id:r.id,...r.data()}));return{payments,history:payments.filter(p=>p.status==='paid')};}
export async function getPlatformSubscriptionAnalytics(){const [plansSnap,companiesSnap]=await Promise.all([getDocs(collection(db,'subscriptionPlans')),getDocs(collection(db,'companies'))]);const plans=plansSnap.docs.map(r=>({id:r.id,...r.data()}));const companies=companiesSnap.docs.map(r=>({id:r.id,...r.data()}));return{plans,companies,totals:{plans:plans.length,companies:companies.length,committedSeats:companies.reduce((s,c)=>s+Number(c.subscription?.usedSeats||0)+Number(c.subscription?.reservedSeats||0),0)}};}

const registrationStateForStage = (stage = '') => {
  const normalized = String(stage || 'account').toLowerCase();
  if (normalized === 'completed' || normalized === 'active') {
    return { registrationStage: 'completed', registrationStatus: 'active', registrationIncomplete: false, profileCompleted: true };
  }
  if (normalized.includes('extra_seat')) {
    return { registrationStage: 'extra_seat_payment', registrationStatus: 'pending_extra_seat_payment', registrationIncomplete: true, profileCompleted: true };
  }
  if (normalized.includes('company_approval')) {
    return { registrationStage: 'company_approval', registrationStatus: 'pending_company_approval', registrationIncomplete: true, profileCompleted: true };
  }
  if (normalized.includes('company_subscription') || normalized.includes('payment')) {
    return { registrationStage: normalized, registrationStatus: 'pending_company_subscription', registrationIncomplete: true, profileCompleted: true };
  }
  if (normalized.includes('business_interests') || normalized.includes('interest')) {
    return { registrationStage: 'business_interests', registrationStatus: 'interests_incomplete', registrationIncomplete: true, profileCompleted: false };
  }
  if (normalized.includes('profile')) {
    return { registrationStage: 'profile_completion', registrationStatus: 'profile_incomplete', registrationIncomplete: true, profileCompleted: false };
  }
  if (normalized.includes('verification')) {
    return { registrationStage: 'email_verification', registrationStatus: 'verification_pending', registrationIncomplete: true, profileCompleted: false };
  }
  return { registrationStage: normalized || 'account', registrationStatus: 'incomplete', registrationIncomplete: true, profileCompleted: false };
};

const normalizeCompanyWebsite = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const parsed = new URL(candidate);
    parsed.hash = '';
    parsed.search = '';
    parsed.hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const pathname = parsed.pathname.replace(/\/+$/, '');
    return `${parsed.protocol}//${parsed.hostname}${parsed.port ? `:${parsed.port}` : ''}${pathname}`.replace(/\/+$/, '');
  } catch (_) {
    throw new Error('Enter a valid company website URL.');
  }
};

const companyWebsiteIndexId = (website = '') => encodeURIComponent(String(website || '').toLowerCase());

export async function createRegistrationCompany(payload = {}) {
  const uid = currentUid();
  const company = payload.company || {};
  const userRef = doc(db, 'users', uid);
  const existingUser = await getDoc(userRef);
  const existingData = existingUser.exists() ? existingUser.data() || {} : {};
  const linkedCompanyId = existingData.registrationCompanyId || (existingData.registrationMode === 'create_company' ? existingData.companyId : '');
  if (linkedCompanyId) {
    const existingCompany = await getDoc(doc(db, 'companies', linkedCompanyId));
    if (existingCompany.exists() && String(existingCompany.data()?.adminId || '') === uid) {
      const existingSubscription = existingCompany.data()?.subscription || {};
      return {
        companyId: linkedCompanyId,
        paymentRequired: String(existingSubscription.status || '').toLowerCase() === 'pending_payment' || existingCompany.data()?.paymentStatus === 'pending',
        subscription: existingSubscription,
        resumed: true,
      };
    }
  }

  let plan = null;
  if (payload.packageId) {
    const planSnap = await getDoc(doc(db, 'subscriptionPlans', payload.packageId));
    if (!planSnap.exists()) throw new Error('The selected subscription plan is no longer available. Refresh and choose an active plan.');
    plan = { id: planSnap.id, ...planSnap.data() };
    if (plan.isActive === false) throw new Error('The selected subscription plan is disabled. Choose another plan.');
  }

  const runtime = await getPublicPlatformRuntime();
  const paymentRequired = Boolean(runtime.registration?.paymentRequired) && Number(plan?.price || 0) > 0;
  const ref = doc(collection(db, 'companies'));
  const rawLimit = Number(plan?.userLimit ?? plan?.maxUsers ?? company.subscription?.userLimit ?? company.subscription?.maxUsers ?? 1);
  const subscription = {
    ...(company.subscription || {}),
    packageId: plan?.id || company.subscription?.packageId || null,
    packageName: plan?.name || company.subscription?.packageName || '',
    price: Number(plan?.price ?? company.subscription?.price ?? 0),
    currency: plan?.currency || company.subscription?.currency || runtime.payments?.currency || 'EUR',
    billingCycle: plan?.billingCycle || company.subscription?.billingCycle || 'monthly',
    userLimit: rawLimit,
    maxUsers: rawLimit,
    unlimitedUsers: Boolean(plan?.unlimitedUsers || rawLimit === 0),
    allowExtraSeats: plan?.unlimitedUsers || rawLimit === 0 ? false : plan?.allowExtraSeats !== false,
    status: paymentRequired ? 'pending_payment' : 'active',
  };

  const normalizedWebsite = normalizeCompanyWebsite(company.website || '');
  const websiteIndexRef = normalizedWebsite
    ? doc(db, 'companyWebsiteIndex', companyWebsiteIndexId(normalizedWebsite))
    : null;

  // Compatibility check for companies created before the website index existed.
  if (normalizedWebsite) {
    const legacyMatches = await getDocs(query(collection(db, 'companies'), where('website', '==', normalizedWebsite)));
    const conflicting = legacyMatches.docs.find((item) => item.id !== ref.id);
    if (conflicting) throw new Error('This company website is already registered on TELCOFAIR.');
  }

  await runTransaction(db, async (transaction) => {
    if (websiteIndexRef) {
      const websiteSnap = await transaction.get(websiteIndexRef);
      const indexedCompanyId = websiteSnap.exists() ? String(websiteSnap.data()?.companyId || '') : '';
      if (indexedCompanyId && indexedCompanyId !== ref.id) {
        throw new Error('This company website is already registered on TELCOFAIR.');
      }
    }

    transaction.set(ref, {
      ...company,
      website: normalizedWebsite || company.website || '',
      websiteNormalized: normalizedWebsite,
      adminId: uid,
      subscription,
      isActive: !paymentRequired,
      paymentStatus: paymentRequired ? 'pending' : 'not_required',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    transaction.set(userRef, {
      uid,
      userId: uid,
      email: String(auth.currentUser?.email || existingData.email || '').trim().toLowerCase(),
      companyId: ref.id,
      registrationCompanyId: ref.id,
      registrationPackageId: plan?.id || payload.packageId || null,
      registrationMode: 'create_company',
      registrationStatus: paymentRequired ? 'pending_company_subscription' : 'company_profile_finalization',
      paymentStatus: paymentRequired ? 'company_payment_pending' : 'not_required',
      registrationStage: paymentRequired ? 'company_subscription' : 'company_profile_finalization',
      stage: paymentRequired ? 'company_subscription' : 'company_profile_finalization',
      registrationIncomplete: true,
      profileCompleted: false,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    if (websiteIndexRef) {
      transaction.set(websiteIndexRef, {
        website: normalizedWebsite,
        companyId: ref.id,
        ownerUid: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
  });
  return { companyId: ref.id, paymentRequired, subscription };
}

export async function saveRegistrationProgress(payload = {}) {
  const uid = currentUid();
  const ref = doc(db, 'users', uid);
  const existingSnap = await getDoc(ref);
  const existing = existingSnap.exists() ? existingSnap.data() || {} : {};
  const existingStatus = String(existing.registrationStatus || '').toLowerCase();
  if (['expired', 'declined'].includes(existingStatus)) {
    throw new Error(existingStatus === 'expired'
      ? 'This registration has expired and must be reactivated by Super Admin before it can be changed.'
      : 'This registration was declined and cannot be changed from the Company Portal.');
  }
  const requestedStage = String(payload.registrationStage || payload.stage || '').toLowerCase();
  const existingStage = String(existing.registrationStage || existing.stage || '').toLowerCase();
  const existingPaymentStatus = String(existing.paymentStatus || '').toLowerCase();
  const protectedPending = [
    'pending_company_subscription', 'company_payment_pending', 'pending_payment',
    'pending_extra_seat_payment', 'pending_extra_seat_payment_review', 'pending_company_approval',
  ].includes(existingStatus)
    || ['company_payment_pending', 'pending', 'extra_seat_payment_pending', 'extra_seat_payment_submitted'].includes(existingPaymentStatus)
    || ['company_subscription', 'extra_seat_payment', 'company_approval'].includes(existingStage);
  const earlyStage = requestedStage && ['account', 'email_verification', 'profile_completion', 'business_interests'].includes(requestedStage);
  if (protectedPending && earlyStage) {
    return {
      success: true,
      preserved: true,
      registrationStage: existing.registrationStage || existing.stage || existingStage,
      registrationStatus: existing.registrationStatus || 'incomplete',
      registrationIncomplete: existing.registrationIncomplete !== false,
      profileCompleted: existing.profileCompleted === true,
      stage: existing.registrationStage || existing.stage || existingStage,
    };
  }
  const stage = payload.registrationStage || payload.stage || existing.registrationStage || existing.stage || 'account';
  const canonical = registrationStateForStage(stage);
  const draft = payload.draft || existing.draft || {};
  const email = String(auth.currentUser?.email || payload.email || existing.email || '').trim().toLowerCase();
  const firstName = payload.firstName ?? draft.firstName ?? existing.firstName ?? '';
  const lastName = payload.lastName ?? draft.lastName ?? existing.lastName ?? '';
  const displayName = payload.displayName || existing.displayName || [firstName, lastName].filter(Boolean).join(' ');
  const startedAt = existing.registrationStartedAt || existing.createdAt || serverTimestamp();
  const verificationFromAuth = Boolean(auth.currentUser?.emailVerified);

  const cleanPayload = { ...payload };
  delete cleanPayload.password;
  delete cleanPayload.confirmPassword;

  await setDoc(ref, {
    ...cleanPayload,
    ...canonical,
    stage: canonical.registrationStage,
    draft,
    uid,
    userId: existing.userId || uid,
    email,
    emailNormalized: email,
    firstName,
    lastName,
    displayName,
    emailVerified: existing.emailVerified === true || verificationFromAuth,
    isVerified: existing.isVerified === true || verificationFromAuth,
    registrationStartedAt: startedAt,
    createdAt: existing.createdAt || serverTimestamp(),
    lastActivityAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return { success: true, ...canonical, stage: canonical.registrationStage };
}

export async function getRegistrationProgress() {
  const uid = currentUid();
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { exists: true, ...snap.data() } : { exists: false };
}

export async function joinExistingCompany({ companyId, profile = {} }) {
  const uid = currentUid();
  const summary = await getSeatSummary(companyId);
  const { seats, company, subscription } = summary;
  if (company.isBlocked === true || company.isActive === false) throw new Error('This company is not currently accepting new registrations.');
  const subscriptionStatus = String(subscription?.status || company.subscriptionStatus || '').toLowerCase();
  const expiryValue = subscription?.expiryDate || subscription?.expiresAt;
  const expiry = expiryValue?.toDate ? expiryValue.toDate() : expiryValue?.seconds ? new Date(expiryValue.seconds * 1000) : expiryValue ? new Date(expiryValue) : null;
  const expired = expiry instanceof Date && !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
  if (expired || ['expired', 'cancelled', 'canceled', 'suspended', 'pending_payment', 'inactive'].includes(subscriptionStatus)) {
    throw new Error('This company does not have an active subscription and cannot accept new members right now.');
  }

  const overLimit = !seats.unlimited && seats.limit > 0 && seats.committed >= seats.limit;
  if (overLimit && !seats.allowExtraSeats) throw new Error('This company has reached its package user limit and the package does not allow additional seats.');
  if (overLimit && Number(seats.extraSeatPrice || 0) <= 0) throw new Error('This company has reached its included user limit but has not configured the additional-user price yet.');
  const needsExtraSeatPayment = overLimit;
  const seatStatus = needsExtraSeatPayment ? 'pending_extra_seat_payment' : 'reserved';
  const registrationStatus = needsExtraSeatPayment ? 'pending_extra_seat_payment' : 'pending_company_approval';
  const joinBatch = writeBatch(db);
  joinBatch.set(doc(db, 'users', uid), {
    ...profile,
    uid,
    email: String(profile.email || auth.currentUser?.email || '').trim().toLowerCase(),
    emailNormalized: String(profile.email || auth.currentUser?.email || '').trim().toLowerCase(),
    companyId,
    companyName: company.companyName || company.name || '',
    role: 'employee',
    isOwner: false,
    isActive: false,
    isApproved: false,
    seatStatus,
    registrationMode: 'join_company',
    registrationStatus,
    paymentStatus: needsExtraSeatPayment ? 'extra_seat_payment_pending' : 'not_required',
    profileCompleted: true,
    registrationIncomplete: true,
    registrationStage: needsExtraSeatPayment ? 'extra_seat_payment' : 'company_approval',
    stage: needsExtraSeatPayment ? 'extra_seat_payment' : 'company_approval',
    extraSeatAmount: needsExtraSeatPayment ? seats.extraSeatPrice : 0,
    extraSeatCurrency: seats.extraSeatCurrency,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  if (needsExtraSeatPayment) {
    joinBatch.set(doc(db, 'companies', companyId, 'extraSeatPayments', uid), {
      userId: uid,
      email: profile.email || auth.currentUser?.email || '',
      amount: seats.extraSeatPrice,
      currency: seats.extraSeatCurrency,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }
  await joinBatch.commit();
  return {
    success: true,
    status: registrationStatus,
    paymentRequired: needsExtraSeatPayment,
    extraSeatAmount: seats.extraSeatPrice,
    extraSeatCurrency: seats.extraSeatCurrency,
    extraSeatPaymentLink: seats.extraSeatPaymentLink,
    extraSeatPaymentInstructions: seats.extraSeatPaymentInstructions,
    companyId,
  };
}

async function getValidatedCompanyMember(companyId, userId) {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) throw new Error('Team member was not found.');
  const user = userSnap.data() || {};
  if (String(user.companyId || '') !== String(companyId || '')) {
    throw new Error('This user does not belong to your company.');
  }
  return { userRef, userSnap, user };
}

export async function getExtraSeatPayment(companyId, userId = currentUid()) {
  const actorUid = currentUid();
  if (String(userId) !== String(actorUid)) {
    await requireCompanyAdmin(companyId);
    await getValidatedCompanyMember(companyId, userId);
  } else {
    const actorSnap = await getDoc(doc(db, 'users', actorUid));
    const actor = actorSnap.exists() ? actorSnap.data() || {} : {};
    if (String(actor.companyId || '') !== String(companyId || '')) throw new Error('This payment does not belong to your company registration.');
  }
  const snap = await getDoc(doc(db, 'companies', companyId, 'extraSeatPayments', userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function submitExtraSeatPayment(companyId, { transactionReference = '', notes = '' } = {}) {
  const uid = currentUid();
  await getValidatedCompanyMember(companyId, uid);
  const ref = doc(db, 'companies', companyId, 'extraSeatPayments', uid);
  const paymentSnap = await getDoc(ref);
  if (!paymentSnap.exists()) throw new Error('The additional-seat payment request was not found.');
  const payment = paymentSnap.data() || {};
  if (['paid', 'approved'].includes(String(payment.status || '').toLowerCase())) return { success: true, alreadyPaid: true };
  await setDoc(ref, {
    transactionReference: String(transactionReference).trim(),
    notes: String(notes).trim(),
    status: 'submitted',
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await setDoc(doc(db, 'users', uid), {
    paymentStatus: 'extra_seat_payment_submitted',
    registrationStatus: 'pending_extra_seat_payment_review',
    registrationIncomplete: true,
    registrationStage: 'extra_seat_payment',
    stage: 'extra_seat_payment',
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return { success: true };
}

export async function markExtraSeatPaymentPaid(companyId, userId, paid = true) {
  await requireCompanyAdmin(companyId);
  const { userRef, user } = await getValidatedCompanyMember(companyId, userId);
  const paymentRef = doc(db, 'companies', companyId, 'extraSeatPayments', userId);
  const paymentSnap = await getDoc(paymentRef);
  if (!paymentSnap.exists()) throw new Error('The additional-seat payment request was not found.');
  const alreadyApproved = Boolean(user.isApproved);
  const batch = writeBatch(db);
  batch.set(paymentRef, {
    status: paid ? 'paid' : 'rejected',
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  batch.set(userRef, {
    paymentStatus: paid ? 'extra_seat_paid' : 'extra_seat_payment_rejected',
    seatStatus: paid ? (alreadyApproved ? 'active' : 'reserved') : 'pending_extra_seat_payment',
    isActive: paid && alreadyApproved,
    registrationStatus: paid ? (alreadyApproved ? 'active' : 'pending_company_approval') : 'pending_extra_seat_payment',
    registrationIncomplete: paid ? !alreadyApproved : true,
    registrationStage: paid ? (alreadyApproved ? 'completed' : 'company_approval') : 'extra_seat_payment',
    stage: paid ? (alreadyApproved ? 'completed' : 'company_approval') : 'extra_seat_payment',
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await batch.commit();
  return { success: true, activated: paid && alreadyApproved };
}

export async function approveCompanyJoinRequest(companyId, userId) {
  await requireCompanyAdmin(companyId);
  const { userRef, user } = await getValidatedCompanyMember(companyId, userId);
  if (['extra_seat_payment_pending', 'extra_seat_payment_submitted', 'extra_seat_payment_rejected'].includes(String(user.paymentStatus || ''))) {
    throw new Error('The additional user-seat payment must be marked paid before approval.');
  }
  await setDoc(userRef, {
    isActive: true, isApproved: true, isBlocked: false, seatStatus: 'active',
    registrationStatus: 'active', registrationIncomplete: false, profileCompleted: true,
    registrationStage: 'completed', stage: 'completed', updatedAt: serverTimestamp(),
  }, { merge: true });
  return { success: true };
}

export async function declineCompanyJoinRequest(companyId, userId) {
  await requireCompanyAdmin(companyId);
  const { userRef } = await getValidatedCompanyMember(companyId, userId);
  await setDoc(userRef, {
    isActive: false, isApproved: false, seatStatus: 'declined', registrationStatus: 'declined',
    registrationIncomplete: true, registrationStage: 'declined', stage: 'declined', updatedAt: serverTimestamp(),
  }, { merge: true });
  return { success: true };
}

export async function updateCompanyMember(companyId, userId, changes = {}) {
  await requireCompanyAdmin(companyId);
  const { userRef, user } = await getValidatedCompanyMember(companyId, userId);
  const safe = {};
  const copy = (key, max = 500) => {
    if (changes[key] === undefined) return;
    const value = changes[key];
    safe[key] = typeof value === 'string' ? value.trim().slice(0, max) : value;
  };
  ['displayName','firstName','lastName','phoneNumber','phone','dialCode','countryCode','designation','department','photoURL'].forEach((key) => copy(key));
  if (changes.photoAsset !== undefined) safe.photoAsset = changes.photoAsset || null;
  if (changes.role !== undefined && user.isOwner !== true) safe.role = sanitizeCompanyMemberRole(changes.role);
  if (changes.permissions !== undefined) safe.permissions = sanitizeCompanyPermissions(changes.permissions);
  if (!Object.keys(safe).length) throw new Error('No editable team member fields were provided.');
  safe.updatedAt = serverTimestamp();
  await setDoc(userRef, safe, { merge: true });
  return { success: true, changes: safe };
}

export async function removeCompanyMember(companyId, userId) {
  await requireCompanyAdmin(companyId);
  const { userRef } = await getValidatedCompanyMember(companyId, userId);
  await setDoc(userRef, {
    companyId: '', companyName: '', isActive: false, isApproved: false, isBlocked: true,
    seatStatus: 'released', registrationStatus: 'removed_from_company', updatedAt: serverTimestamp(),
  }, { merge: true });
  return { success: true };
}

export async function setCompanyMemberStatus(companyId, userId, active) {
  await requireCompanyAdmin(companyId);
  const { userRef, user } = await getValidatedCompanyMember(companyId, userId);
  if (user.isOwner === true && !active) throw new Error('The company owner cannot be disabled from the team member list.');
  if (active && user.isActive !== true) {
    const summary = await getSeatSummary(companyId);
    if (!summary.seats.unlimited && summary.seats.limit > 0 && summary.seats.committed >= summary.seats.limit) {
      throw new Error('The company user limit has been reached.');
    }
  }
  await setDoc(userRef, {
    isActive: Boolean(active), isBlocked: !active, seatStatus: active ? 'active' : 'released', updatedAt: serverTimestamp(),
  }, { merge: true });
  return { success: true };
}

export async function createCompanyMember(companyId, payload = {}) {
  await requireCompanyAdmin(companyId);
  const profile = payload.profile || payload || {};
  const email = String(profile.email || payload.email || '').trim().toLowerCase();
  const password = String(payload.password || '');
  if (!email) throw new Error('Team member email is required.');
  if (password.length < 8) throw new Error('Temporary password must contain at least 8 characters.');

  const summary = await getSeatSummary(companyId);
  const requestedActive = payload.isActive !== false && String(profile.status || 'active').toLowerCase() !== 'inactive';
  const over = requestedActive && !summary.seats.unlimited && summary.seats.limit > 0 && summary.seats.committed >= summary.seats.limit;
  if (over && !summary.seats.allowExtraSeats) throw new Error('The company package user limit has been reached and this package does not allow additional paid seats.');
  if (over && Number(summary.seats.extraSeatPrice || 0) <= 0) throw new Error('The included user limit is full. Configure a positive additional-user price in Subscription Settings before adding another member.');

  const secondary = initializeApp(app.options, `company-member-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
  const secondaryAuth = getAuth(secondary);
  let createdUser = null;
  try {
    const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    createdUser = credential.user;
    const uid = createdUser.uid;
    const needsPayment = over;

    // Persist the member and any required extra-seat payment record together.
    // If this write fails, roll back the Auth identity while we still own its
    // secondary Firebase session so an invisible/orphan account is not left.
    const batch = writeBatch(db);
    batch.set(doc(db, 'users', uid), {
      uid,
      userId: uid,
      email,
      emailNormalized: email,
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      displayName: profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
      phoneNumber: profile.phoneNumber || profile.phone || '',
      phone: profile.phone || profile.phoneNumber || '',
      dialCode: profile.dialCode || '',
      countryCode: profile.countryCode || '',
      designation: profile.designation || '',
      department: profile.department || '',
      photoURL: profile.photoURL || null,
      photoAsset: profile.photoAsset || null,
      companyId,
      companyName: summary.company.companyName || '',
      role: sanitizeCompanyMemberRole(payload.role || profile.role || 'employee'),
      permissions: sanitizeCompanyPermissions(payload.permissions || profile.permissions || ['read']),
      isActive: requestedActive && !needsPayment,
      isBlocked: !requestedActive,
      isApproved: true,
      isVerified: false,
      emailVerified: false,
      seatStatus: !requestedActive ? 'released' : needsPayment ? 'pending_extra_seat_payment' : 'active',
      paymentStatus: needsPayment ? 'extra_seat_payment_pending' : 'not_required',
      extraSeatAmount: needsPayment ? summary.seats.extraSeatPrice : 0,
      extraSeatCurrency: summary.seats.extraSeatCurrency,
      verificationDeliveryStatus: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });

    if (needsPayment) {
      batch.set(doc(db, 'companies', companyId, 'extraSeatPayments', uid), {
        userId: uid,
        email,
        amount: summary.seats.extraSeatPrice,
        currency: summary.seats.extraSeatCurrency,
        status: 'pending',
        paymentLink: summary.seats.extraSeatPaymentLink || '',
        instructions: summary.seats.extraSeatPaymentInstructions || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }

    try {
      await batch.commit();
    } catch (profileError) {
      await deleteUser(createdUser).catch(() => {});
      createdUser = null;
      throw profileError;
    }

    const runtime = await getPublicPlatformRuntime().catch(() => ({}));
    const verificationProvider = runtime?.auth?.verificationProvider || 'firebase_auth';
    let verificationWarning = '';
    try {
      if (verificationProvider === 'firebase_auth') {
        await sendEmailVerification(credential.user);
      } else if (verificationProvider === 'professional_email') {
        const endpoint = runtime.email?.verificationGatewayUrl || runtime.email?.gatewayUrl || '';
        if (!endpoint) throw new Error('Professional email verification gateway is not configured.');
        await postPublicGateway(endpoint, {
          channel: 'email', action: 'send_verification', operation: 'send_verification', uid, email,
          phone: profile.phone || profile.phoneNumber || '', firstName: profile.firstName || '',
          appUrl: runtime.email?.verificationBaseUrl || window.location.origin,
        });
      } else if (verificationProvider === 'whatsapp') {
        const phone = profile.phone || profile.phoneNumber || '';
        if (!phone) throw new Error('A phone number is required when WhatsApp verification is selected.');
        await requestWhatsAppOtp(phone, 'verification');
      }
      await setDoc(doc(db, 'users', uid), {
        verificationMethod: verificationProvider,
        verificationDeliveryStatus: verificationProvider === 'manual' ? 'manual_pending' : 'sent',
        verificationDeliveryError: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (verificationError) {
      verificationWarning = friendlyError(verificationError, 'Team member was created, but verification delivery failed.');
      await setDoc(doc(db, 'users', uid), {
        verificationMethod: verificationProvider,
        verificationDeliveryStatus: 'failed',
        verificationDeliveryError: verificationWarning,
        updatedAt: serverTimestamp(),
      }, { merge: true }).catch(() => {});
    }

    await firebaseSignOut(secondaryAuth).catch(() => {});
    return {
      success: true,
      userId: uid,
      email,
      paymentRequired: needsPayment,
      extraSeatAmount: summary.seats.extraSeatPrice,
      extraSeatCurrency: summary.seats.extraSeatCurrency,
      extraSeatPaymentLink: summary.seats.extraSeatPaymentLink,
      extraSeatPaymentInstructions: summary.seats.extraSeatPaymentInstructions,
      verificationWarning,
    };
  } finally {
    await firebaseSignOut(secondaryAuth).catch(() => {});
    await deleteApp(secondary).catch(() => {});
  }
}

export async function createRegistrationPaymentOrder(payload){const runtime=await getPublicPlatformRuntime();if(!runtime.registration?.paymentRequired)return{alreadyPaid:true,activated:true,provider:'disabled'};return call('createRegistrationPaymentOrder',payload);}
export async function getRegistrationPaymentStatus(){const uid=currentUid();const snap=await getDoc(doc(db,'users',uid));const user=snap.exists()?snap.data()||{}:{};if(user.paymentStatus==='not_required'||user.registrationStatus==='active')return{active:true,status:user.registrationStatus||'active'};return call('getRegistrationPaymentStatus',{});}
export const createCompanySubscriptionOrder=(companyId,planId,origin=window.location.origin)=>call('createCompanySubscriptionOrder',{companyId,planId,origin});

export const getCalendarConnections=()=>call('getCalendarConnections',{});
export const startCalendarOAuth=(provider,origin=window.location.origin)=>call('startCalendarOAuth',{provider,origin});
export const disconnectCalendar=(provider)=>call('disconnectCalendar',{provider});
export const getExternalCalendarBusy=(companyId,payload)=>call('getExternalCalendarBusy',{companyId,...payload});
export const createCalendarFeed=()=>call('createCalendarFeed',{});

export default call;
